/**
 * Auto-pack readiness — whether remaining fulfilment qty can be planned.
 *
 * Catalogue facts still resolve via `resolveShippingFacts` /
 * `isCompleteShipping`. This module only decides the operator-facing block
 * and message; it does not change parcelization math.
 */

import {
  isCompleteShipping,
  missingShippingFields,
  shippingFieldLabel,
  type ShippingDimensions,
  type ShippingFactField,
} from "./shipping-facts";

export interface AutoPackSourceLine {
  id: string;
  name: string;
  remainingQty: number;
  facts: ShippingDimensions;
}

export interface IncompleteAutoPackLine {
  name: string;
  missing: ShippingFactField[];
}

export const NO_REMAINING_PACK_MESSAGE = "No remaining items to pack.";

export function incompleteAutoPackMessage(incomplete: IncompleteAutoPackLine[]): string {
  const detail = incomplete
    .map((row) => `${row.name} (missing ${row.missing.map(shippingFieldLabel).join(", ")})`)
    .join("; ");
  return `Shipping information incomplete — can't auto-pack. ${detail}. Enter package weight/dimensions manually, or update the catalogue.`;
}

export function evaluateAutoPackReadiness(lines: AutoPackSourceLine[]): {
  canAutoPack: boolean;
  remainingCount: number;
  incomplete: IncompleteAutoPackLine[];
  feedback: string | null;
} {
  const remaining = lines.filter((line) => line.remainingQty > 0);
  if (remaining.length === 0) {
    return {
      canAutoPack: false,
      remainingCount: 0,
      incomplete: [],
      feedback: NO_REMAINING_PACK_MESSAGE,
    };
  }
  const incomplete = remaining
    .filter((line) => !isCompleteShipping(line.facts))
    .map((line) => ({
      name: line.name,
      missing: missingShippingFields(line.facts),
    }));
  if (incomplete.length > 0) {
    return {
      canAutoPack: false,
      remainingCount: remaining.length,
      incomplete,
      feedback: incompleteAutoPackMessage(incomplete),
    };
  }
  return {
    canAutoPack: true,
    remainingCount: remaining.length,
    incomplete: [],
    feedback: null,
  };
}
